import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/Button';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import { ThreeDSecureModal } from '@/components/ThreeDSecureModal';
import { ValidationErrorNotification } from '@/components/ValidationErrorNotification';
import { ButtonVariant, PaymentOption } from '@/constants';
import { useCheckout } from '@/contexts/checkoutContext';
import { usePageData } from '@/contexts/pageContext';
import { useValidation } from '@/contexts/validationContext';
import { useNoBalance } from '@/hooks/useNoBalance';
import { usePayPal } from '@/hooks/usePayPal';
import { useSagePay } from '@/hooks/useSagePay';
import { useThreeDSecureCompletion } from '@/hooks/useThreeDSecureCompletion';

export const PayNowButton = () => {
  const [threeDSecureUrl, setThreeDSecureUrl] = useState<string | null>(null);
  const [currentTransactionId, setCurrentTransactionId] = useState<
    string | null
  >(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    checkoutData: { hasBalance },
    paymentOption,
  } = useCheckout();

  const { hasValidationError, validatePayload } = useValidation();

  const { createPayPalOrder, isPending: isPayPalPending } = usePayPal();

  const { createCreditCardOrder, isPending: isSagePayPending } = useSagePay({
    onThreeDSecureRequired: url => {
      console.log('Credit Card: 3D Secure required, showing modal');
      setThreeDSecureUrl(url);
      // Extract transaction ID from URL if needed
      const urlParams = new URLSearchParams(url.split('?')[1]);
      const transactionId =
        urlParams.get('transactionId') || urlParams.get('MD') || '';
      setCurrentTransactionId(transactionId);
    },
  });

  const { createNoBalanceOrder, isPending: isNoBalancePending } =
    useNoBalance();
  const { termsConditions } = usePageData();

  const { completeThreeDSecure, isPending: isCompletingThreeDSecure } =
    useThreeDSecureCompletion();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  // Handle 3D Secure completion
  const handleThreeDSecureComplete = useCallback(
    (success: boolean) => {
      console.log('Credit Card: 3D Secure completed', { success });

      if (success && currentTransactionId) {
        // Clear only 3DS modal states
        setThreeDSecureUrl(null);
        setCurrentTransactionId(null);

        // Ensure loading overlay is visible while the final order is being created
        setIsProcessingPayment(true);

        // Reset/extend timeout so the overlay persists during the second API call
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }
        processingTimeoutRef.current = setTimeout(() => {
          console.warn(
            'Credit Card: Post-3DS processing timeout reached, clearing loading state',
          );
          setIsProcessingPayment(false);
        }, 30000);

        // Complete the order after successful 3DS
        completeThreeDSecure(
          {
            transactionId: currentTransactionId,
          },
          {
            onError: () => {
              // Clear loading state only on error
              console.log('Credit Card: 3DS order completion failed');
              setIsProcessingPayment(false);
              if (processingTimeoutRef.current) {
                clearTimeout(processingTimeoutRef.current);
              }
            },
            // Note: We don't use onSettled to keep loading state active until redirect
          },
        );
      } else {
        // Clear all states on failure or user cancellation
        setThreeDSecureUrl(null);
        setCurrentTransactionId(null);
        setIsProcessingPayment(false);
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }

        if (!success) {
          NotificationsQueue.add(
            {
              notification: (
                <NotificationMessage $isError={true}>
                  Payment authentication failed. Please try again.
                </NotificationMessage>
              ),
            },
            { timeout: 5000 },
          );
        }
      }
    },
    [currentTransactionId, completeThreeDSecure],
  );

  const getPayNowHandler = () => {
    if (isPayPalPending || isSagePayPending || isNoBalancePending) {
      return undefined;
    }

    if (hasBalance) {
      if (paymentOption === PaymentOption.CreditCard) {
        return createCreditCardOrder();
      }

      if (paymentOption === PaymentOption.PayPal) {
        return createPayPalOrder();
      }
    }

    return createNoBalanceOrder();
  };

  const handlePayNow = () => {
    if (validatePayload()) {
      getPayNowHandler();
    }
  };

  return (
    <>
      {hasValidationError ? <ValidationErrorNotification /> : null}
      <Button
        handler={handlePayNow}
        isPending={
          isPayPalPending ||
          isSagePayPending ||
          isNoBalancePending ||
          isProcessingPayment ||
          isCompletingThreeDSecure
        }
        variant={[ButtonVariant.FullWidth, ButtonVariant.Primary]}
      >
        Pay now
      </Button>
      <div
        // biome-ignore lint/security/noDangerouslySetInnerHtml: we trust the source of the HTML
        dangerouslySetInnerHTML={{
          __html: termsConditions
            ? termsConditions
            : '<p>By placing your order, you confirm you have read and accepted our terms & conditions and privacy policy</p>',
        }}
      />

      {threeDSecureUrl && (
        <ThreeDSecureModal
          url={threeDSecureUrl}
          onComplete={handleThreeDSecureComplete}
          onClose={() => {
            // User closed modal without completing
            handleThreeDSecureComplete(false);
          }}
        />
      )}
    </>
  );
};
